import { useNavigate } from "@solidjs/router";
import type { Notification } from "@storytell/client";
import { TbX } from "solid-icons/tb";
import { type Component, onCleanup, onMount } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { getNotificationDisplay, getNotificationHref, type NotificationDisplay } from "../notificationDisplay";
import { getNotificationIcon } from "../notificationIcon";

const TOAST_DISMISS_MS = 8000;

export const NotificationToast: Component<{ notification: Notification }> = (props) => {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();

  // Plain functions, not memos: memo reads can yield undefined mid-transition
  // (ENG-6676), and icon() is read unguarded below.
  const display = () => getNotificationDisplay(props.notification);
  const icon = () => getNotificationIcon(props.notification);
  const withRole = (d: NotificationDisplay) => (d.roleKey ? { ...d.params, role: t(d.roleKey) } : d.params);
  const title = () => {
    const d = display();
    return d ? t(d.titleKey, withRole(d)) : props.notification.fallbackTitle;
  };
  const body = () => {
    const d = display();
    return d ? t(d.bodyKey, withRole(d)) : props.notification.fallbackBody;
  };

  let dismissTimer: number | undefined;
  let hoverPaused = false;

  const clearTimer = () => {
    if (dismissTimer !== undefined) {
      clearTimeout(dismissTimer);
      dismissTimer = undefined;
    }
  };

  const scheduleDismiss = () => {
    clearTimer();
    if (hoverPaused) return;
    dismissTimer = window.setTimeout(() => {
      dismissTimer = undefined;
      wire.notifications.dismissToast(props.notification.id);
    }, TOAST_DISMISS_MS);
  };

  onMount(() => {
    scheduleDismiss();
    onCleanup(clearTimer);
  });

  const handleOpen = () => {
    trackFeature({ feature: "notifications", action: "click", via: "button" });
    wire.notifications.markRead(props.notification.id);
    wire.notifications.dismissToast(props.notification.id);
    navigate(getNotificationHref(props.notification));
  };

  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    wire.notifications.dismissToast(props.notification.id);
  };

  return (
    <div
      class="pointer-events-auto flex w-80 items-start gap-3 rounded-xl border border-primary-border bg-tile p-3 shadow-xl"
      onMouseEnter={() => {
        hoverPaused = true;
        clearTimer();
      }}
      onMouseLeave={() => {
        hoverPaused = false;
        scheduleDismiss();
      }}
    >
      <Dynamic component={icon().icon} class={`mt-0.5 size-4 shrink-0 ${icon().tone}`} aria-hidden={true} />
      <button type="button" onClick={handleOpen} class="min-w-0 flex-1 text-left focus:outline-none">
        <span class="block truncate text-sm font-semibold text-primary-text">{title()}</span>
        <span class="mt-0.5 line-clamp-2 text-xs text-secondary-text">{body()}</span>
      </button>
      <button
        type="button"
        onClick={handleClose}
        aria-label={t("common.close")}
        class="shrink-0 rounded-md p-1 text-muted-text transition-colors hover:bg-inset hover:text-primary-text focus:outline-none"
      >
        <TbX class="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
